"use client";
import { useState } from "react";
import { BsCheck2, BsFillXCircleFill, BsInfoCircle } from "react-icons/bs";
import Image from "next/image";
import { Tooltip } from "../components";
import type { deviceFeatureItem, Translationtype } from "utils";
import { abril, poppins } from "utils";

type selectOptionsType = {
  label: string;
  value: string | number | any;
};

interface IClientDeviceTypeSelectProps {
  t: Translationtype;
  register: any;
  errors: any;
  className?: string;
  disabled?: boolean;
  placeholder?: string;
  noTranslate?: boolean;
  name: string;
  title: string;
  disabledOption?: number | string | any;
  defaultValue?: number | string | any;
  mapOptions?: (op: any) => void;
  tooltip?: string;
  options: selectOptionsType[] | any;
  devicesList: any;
}

export function ClientDeviceTypeSelect({
  t,
  register,
  errors,
  className,
  disabled,
  noTranslate,
  options,
  name,
  mapOptions,
  disabledOption,
  defaultValue,
  tooltip,
  title,
  devicesList,
}: IClientDeviceTypeSelectProps) {
  const [showDevices, setShowDevices] = useState<boolean>(false);
  const formatOptions = (opt: any): selectOptionsType[] => {
    if (options && mapOptions) {
      return opt.map((item: any) => mapOptions(item));
    }
    return opt;
  };

  const _options = formatOptions(options);
  return (
    <>
      <div className={`${className || ""} w-full`}>
        <div className="flex flex-row items-end gap-2">
          <div className="w-full">
            <label htmlFor={name} className="w-full block mb-1 text-textColor">
              {title} {tooltip ? <Tooltip text={tooltip} /> : null}
            </label>

            <select
              key={_options}
              id={name}
              disabled={disabled || false}
              defaultValue={defaultValue || ""}
              className={`${
                className || ""
              } select input input-bordered w-full focus:outline-purple-950 cursor-pointer`}
              {...register(name)}
            >
              {_options?.length === 0 ? (
                <option disabled className="bg-gray-200">
                  {t("Form.noSelectOptions")}
                </option>
              ) : (
                <>
                  <option className="bg-gray-200" disabled value="">
                    {t("Form.select")}
                  </option>
                  {_options.map((option, index) => {
                    return (
                      <option
                        value={option.value}
                        key={index}
                        disabled={disabledOption === option.value || false}
                        className={
                          disabledOption === option.value ? "bg-gray-200" : ""
                        }
                      >
                        {noTranslate
                          ? option.label
                          : t(`Options.${option.label}` as any)}
                      </option>
                    );
                  })}
                </>
              )}
            </select>
          </div>
          <button
            type="button"
            className="btn btn-cancel"
            onClick={() => setShowDevices(!showDevices)}
          >
            <BsInfoCircle className="text-lg" />
          </button>
        </div>

        {errors[name]?.message ? (
          <p className="text-sm my-3 text-error">
            {t(`${errors[name]?.message}` as any)}
          </p>
        ) : null}
      </div>
      {showDevices ? (
        <div className="flex flex-col lg:flex-row gap-5 my-5 justify-center">
          {devicesList?.map((item: any, idx: number) => (
            <div
              key={`device_${idx}`}
              className="relative cursor-default flex flex-col items-center max-w-md p-4 my-0 border border-solid rounded-lg md:p-6 md:min-w-[350px]"
            >
              <h3
                className={`${abril.className} m-0 text-2xl font-bold leading-tight tracking-tight text-textColor`}
              >
                {t(`${item.title}` as any)}
              </h3>
              <Image
                src={item.imgSrc}
                priority
                alt="device"
                className="mx-auto mt-4 rounded-lg"
                width={300}
                height={200}
              />
              <div className="flex flex-col items-center gap-3 mt-4 leading-7  py-4">
                <p
                  className={`${poppins.className} box-border m-0 text-2xl text-textColor font-bold leading-none border-solid`}
                >
                  {t(`${item.price}` as any)}
                </p>
                <p
                  className={`${poppins.className} box-border m-0 text-lg text-gray-400 font-semibold leading-none border-solid`}
                >
                  + {t(`${item.advance}` as any)}
                </p>
              </div>
              <ul className="flex-1 p-0 mt-4 md:ml-5 leading-7 ">
                {item.keyFeatures.map(
                  (feature: deviceFeatureItem, _idx: number) => (
                    <li
                      key={`feature_${_idx}`}
                      className={`${poppins.className} inline-flex items-center w-full mb-2 md:ml-5 text-textColor text-left border-solid`}
                    >
                      {feature.available ? (
                        <BsCheck2 className="text-lg mr-2 text-green-900" />
                      ) : (
                        <BsFillXCircleFill className="text-lg mr-2 text-red-900" />
                      )}
                      {t(feature.title as any)}
                    </li>
                  )
                )}
              </ul>
            </div>
          ))}
        </div>
      ) : null}
    </>
  );
}
